import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";
import { Timeout } from "../decorators/timeout.decorator";
import { S25WsNode } from "../pojo/S25WsNode";

export class UserService {
    @Timeout
    private static putUser(username: string, payload: any) {
        return UserService.getUser(username).then((user) => {
            const url = "/r25user.json?r25_username=" + username;

            const isPasswordChanged = !!payload.r25_password;
            S25Util.coalesceDeep(payload, user);
            if (!isPasswordChanged) {
                delete payload.r25_password; //ANG-6728 - only send password node when changing passwords to avoid triggering a random password
            }
            return DataAccess.put(
                DataAccess.injectCaller(url, "UserService.putUser"),
                S25Util.getPayload("r25users", "r25_user", "username", "mod", user, payload),
            );
        });
    }

    @Timeout
    public static deleteUser(username: string) {
        const url = `/r25user.json?${DataAccess.params({ r25_username: username })}`;
        return DataAccess.delete(DataAccess.injectCaller(url, "UserService.deleteUser"));
    }

    @Timeout
    public static postUser(username: string, payload: any) {
        return UserService.getUser(username)
            .then((resp) => resp)
            .catch(() => {
                // return username already in use or catch error when no user returned to post new user
                const data = {
                    r25users: {
                        r25_user: payload,
                    },
                };
                return DataAccess.post(DataAccess.injectCaller("/r25users.json", "UserService.postUser"), data);
            });
    }

    @Timeout
    public static async putUsername(contactId: number, username: string) {
        const payload = {
            new_username: username,
        };

        return DataAccess.put(
            DataAccess.injectCaller(`/micro/contact/${contactId}/username.json`, "UserService.putUsername"),
            payload,
        );
    }

    @Timeout
    public static getUser(username: string) {
        return DataAccess.get(
            DataAccess.injectCaller(
                "/r25user.json?r25_username=" + encodeURIComponent(username),
                "UserService.getUser",
            ),
        ).then((resp) => {
            return resp && resp.r25users && resp.r25users.r25_user && resp.r25users.r25_user[0];
        });
    }

    @Timeout
    public static getUserByContactId(contactId: number) {
        const url = `/r25users.json?${DataAccess.params({ contact_id: contactId, scope: "extended" })}`;
        return DataAccess.get(DataAccess.injectCaller(url, "UserService.getUserByContactId")).then(
            (resp: any) => resp?.r25users?.r25_user?.[0] ?? null,
        );
    }

    //One of those del/new services
    public static setSecurityGroup(username: string, groupId: number, groupName?: string) {
        return UserService.getUser(username).then((user) => {
            user.security_group = S25Util.array.forceArray(user.security_group);
            user.security_group[0].status = "del";
            user.security_group.push({
                status: "new",
                security_group_id: groupId,
                security_group_name: groupName,
            });
            delete user.r25_password;
            const payload = {
                r25users: {
                    r25_user: user,
                },
            };

            return DataAccess.put(
                DataAccess.injectCaller(
                    "/r25user.json?r25_username=" + encodeURIComponent(username),
                    "UserService.putUser",
                ),
                payload,
            );
        });
    }

    public static setActiveState(username: string, state: boolean) {
        const payload = {
            active: state ? "1" : "0",
        };
        return UserService.putUser(username, payload);
    }

    public static getUserSessions(usernameToFilter?: string): Promise<UserSession[]> {
        return DataAccess.get(
            DataAccess.injectCaller("/user_sessions/by_user.json", "UserService.getUserSessions"),
        ).then((sessions: UserSession[]) => {
            sessions ??= [];
            if (usernameToFilter !== undefined) {
                sessions = sessions.filter((s) => s.username !== usernameToFilter);
            }
            sessions = sessions.sort(S25Util.shallowSort("username"));
            return sessions;
        });
    }
}

export type UserSession = {
    count?: number;
    computer: string;
    contId: string | number;
    contactName: string;
    email: string;
    logonDate: string;
    session_id: string | number;
    status: S25WsNode["status"];
    username: string;
};
